export type CalculationResponse = {
  tm: MeltTemperatureParam;
  gc: string;
  extCoef: number;
  mw: number;
  nmolOd: string;
  yield: YieldParam;
};

export type MeltTemperatureParam = {
  mt5Prime: string;
  mt10Prime: string;
};

export type YieldParam = {
  ug: string;
  nmole: string;
};

export type MeltTemperatureMod = {
  S_A_A: number,
  S_A_C: number,
  S_A_G: number,
  S_A_T: number,
  S_A_N: number,

  S_C_A: number,
  S_C_C: number,
  S_C_G: number,
  S_C_T: number,
  S_C_N: number,

  S_G_A: number,
  S_G_C: number,
  S_G_G: number,
  S_G_T: number,
  S_G_N: number,

  S_T_A: number,
  S_T_C: number,
  S_T_G: number,
  S_T_T: number,
  S_T_N: number,

  S_N_A: number,
  S_N_C: number,
  S_N_G: number,
  S_N_T: number,
  S_N_N: number,

  H_A_A: number,
  H_A_C: number,
  H_A_G: number,
  H_A_T: number,
  H_A_N: number,

  H_C_A: number,
  H_C_C: number,
  H_C_G: number,
  H_C_T: number,
  H_C_N: number,

  H_G_A: number,
  H_G_C: number,
  H_G_G: number,
  H_G_T: number,
  H_G_N: number,

  H_T_A: number,
  H_T_C: number,
  H_T_G: number,
  H_T_T: number,
  H_T_N: number,

  H_N_A: number,
  H_N_C: number,
  H_N_G: number,
  H_N_T: number,
  H_N_N: number,
};

export type SeqMapParam = {
  1: number,
  2: number,
  3: number,
  4: number,
  5: number,
  6: number,
  A: number,
  AC: number,
  AT: number,
  B: number,
  C: number,
  D: number,
  DA: number,
  DB: number,
  DC: number,
  DD: number,
  DG: number,
  DH: number,
  DI: number,
  DK: number,
  DM: number,
  DN: number,
  DR: number,
  DS: number,
  DT: number,
  DV: number,
  DW: number,
  DX: number,
  DY: number,
  E: number,
  F: number,
  FC: number,
  FT: number,
  G: number,
  H: number,
  HC: number,
  HT: number,
  I: number,
  J: number,
  JC: number,
  JT: number,
  K: number,
  M: number,
  MEA: number,
  MEC: number,
  MEG: number,
  MEU: number,
  N: number,
  O: number,
  OA: number,
  OC: number,
  OG: number,
  OU: number,
  Q: number,
  R: number,
  S: number,
  T: number,
  TC: number,
  TT: number,
  U: number,
  V: number,
  W: number,
  X: number,
  Y: number,
  Z: number,
};

export type Mod5MapParam = {
  488: number,
  532: number,
  546: number,
  555: number,
  594: number,
  647: number,
  660: number,
  680: number,
  750: number,
  ACR: number,
  ALD: number,
  ALP: number,
  AMC: number,
  BDA: number,
  BDB: number,
  BDC: number,
  BDE: number,
  BDF: number,
  BDG: number,
  BDH: number,
  BDI: number,
  BDJ: number,
  BDK: number,
  BDL: number,
  BDM: number,
  BDN: number,
  BDP: number,
  BFM: number,
  BHX: number,
  BIO: number,
  BOC: number,
  BP2: number,
  BP3: number,
  BP4: number,
  BTT: number,
  CSB: number,
  CY5: number,
  DIP: number,
  FAM: number,
  FLO: number,
  GWF: number,
  GWR: number,
  HEX: number,
  HRP: number,
  MNB: number,
  OGA: number,
  OGB: number,
  OGC: number,
  OGD: number,
  PFB: number,
  PHO: number,
  PSO: number,
  RGA: number,
  RGB: number,
  RGC: number,
  ROX: number,
  RRA: number,
  TAM: number,
  TET: number,
  THL: number,
  TRX: number,
};

export type Mod3MapParam = {
  ADT: number,
  AM7: number,
  AMN: number,
  BIO: number,
  CPG: number,
  DAB: number,
  TAM: number,
};

export type CutoffTimeDuration = {
  hrs: number;
  mins: number;
  secs: number;
  rollOver?: boolean;
};

export interface CalculationService {
  getOligoSequenceCalculations(sequence: string, od: number): ng.IPromise<CalculationResponse>;
  getRemainingCutoffTime(cutoffTime: number): CutoffTimeDuration;
}

export class CalculationServiceImpl implements CalculationService {
  constructor(private $q: ng.IQService) {
    // constructor
  }

  public getOligoSequenceCalculations(sequence: string, od: number): ng.IPromise<CalculationResponse> {
    const deferred: ng.IDeferred<CalculationResponse> = this.$q.defer();

    if (!sequence) {
      sequence = '';
    }
    sequence = sequence.toUpperCase();

    const response: CalculationResponse = {
      tm: this.calculateMeltTemperature(sequence),
      gc: this.calculateGcPercentage(sequence),
      extCoef: this.calculateExtinctionCoefficient(sequence),
      mw: this.calculateMolecularWeight(sequence),
      nmolOd: this.calculateSequenceNmoleOd(sequence),
      yield: this.calculateYield(sequence, od),
    };

    deferred.resolve(response);
    return deferred.promise;
  }

  public getRemainingCutoffTime(cutoffTime: number): CutoffTimeDuration {
    const currentTime: number = new Date().getTime();
    const seconds: number = (cutoffTime - currentTime) / 1000;
    const mins: number = seconds / 60;
    const hrs: number = mins / 60;
    const remainHrs: number = Math.floor(hrs);
    const minsLeft: number = (remainHrs === 0 ? hrs : (hrs % remainHrs)) * 60;
    const remainMins: number = Math.floor(minsLeft);
    const secsLeft: number = (remainMins === 0 ? minsLeft : (minsLeft % remainMins)) * 60;
    const remainSecs: number = Math.floor(secsLeft);

    return { hrs: remainHrs, mins: remainMins, secs: remainSecs };
  }

  private calculateMeltTemperature(sequence: string): MeltTemperatureParam {
    const modifiers: MeltTemperatureMod = {
      S_A_A: 240,
      S_A_C: 173,
      S_A_G: 208,
      S_A_T: 239,
      S_A_N: 215,

      S_C_A: 129,
      S_C_C: 266,
      S_C_G: 278,
      S_C_T: 208,
      S_C_N: 220,

      S_G_A: 135,
      S_G_C: 267,
      S_G_G: 266,
      S_G_T: 173,
      S_G_N: 210,

      S_T_A: 169,
      S_T_C: 135,
      S_T_G: 129,
      S_T_T: 240,
      S_T_N: 168,

      S_N_A: 168,
      S_N_C: 210,
      S_N_G: 220,
      S_N_T: 215,
      S_N_N: 203,

      H_A_A: 91,
      H_A_C: 65,
      H_A_G: 78,
      H_A_T: 86,
      H_A_N: 80,

      H_C_A: 58,
      H_C_C: 110,
      H_C_G: 119,
      H_C_T: 78,
      H_C_N: 91,

      H_G_A: 56,
      H_G_C: 111,
      H_G_G: 110,
      H_G_T: 65,
      H_G_N: 85,

      H_T_A: 60,
      H_T_C: 56,
      H_T_G: 58,
      H_T_T: 91,
      H_T_N: 66,

      H_N_A: 66,
      H_N_C: 85,
      H_N_G: 91,
      H_N_T: 80,
      H_N_N: 80,
    };

    let oligoTM1M: number = 0;
    let oligoTM05M: number = 0;

    if (sequence) {
      let dh: number = 0;
      let ds: number = 108;
      const seq: string = sequence.replace('\\[.*\\]', 'N');
      const seqLen: number = seq.length;

      for (let i: number = 0; i < seqLen - 1; i++) {
        const cLast: string = seq.charAt(i);
        const cThis: string = seq.charAt(i + 1);

        if (cThis !== ' ') {
          if (cLast === 'A') {
            if (cThis === 'A') {
              dh += modifiers.H_A_A;
              ds += modifiers.S_A_A;

            } else if (cThis === 'T') {
              dh += modifiers.H_A_T;
              ds += modifiers.S_A_T;
            } else if (cThis === 'G') {
              dh += modifiers.H_A_G;
              ds += modifiers.S_A_G;
            } else if (cThis === 'C') {
              dh += modifiers.H_A_C;
              ds += modifiers.S_A_C;
            } else {
              dh += modifiers.H_A_N;
              ds += modifiers.S_A_N;
            }
          } else if (cLast === 'T') {
            if (cThis === 'A') {
              dh += modifiers.H_T_A;
              ds += modifiers.S_T_A;
            } else if (cThis === 'T') {
              dh += modifiers.H_T_T;
              ds += modifiers.S_T_T;
            } else if (cThis === 'G') {
              dh += modifiers.H_T_G;
              ds += modifiers.S_T_G;
            } else if (cThis === 'C') {
              dh += modifiers.H_T_C;
              ds += modifiers.S_T_C;
            } else {
              dh += modifiers.H_T_N;
              ds += modifiers.S_T_N;
            }
          } else if (cLast === 'G') {
            if (cThis === 'A') {
              dh += modifiers.H_G_A;
              ds += modifiers.S_G_A;
            } else if (cThis === 'T') {
              dh += modifiers.H_G_T;
              ds += modifiers.S_G_T;
            } else if (cThis === 'G') {
              dh += modifiers.H_G_G;
              ds += modifiers.S_G_G;
            } else if (cThis === 'C') {
              dh += modifiers.H_G_C;
              ds += modifiers.S_G_C;
            } else {
              dh += modifiers.H_G_N;
              ds += modifiers.S_G_N;
            }
          } else if (cLast === 'C') {
            if (cThis === 'A') {
              dh += modifiers.H_C_A;
              ds += modifiers.S_C_A;
            } else if (cThis === 'T') {
              dh += modifiers.H_C_T;
              ds += modifiers.S_C_T;
            } else if (cThis === 'G') {
              dh += modifiers.H_C_G;
              ds += modifiers.S_C_G;
            } else if (cThis === 'C') {
              dh += modifiers.H_C_C;
              ds += modifiers.S_C_C;
            } else {
              dh += modifiers.H_C_N;
              ds += modifiers.S_C_N;
            }
          } else if (cThis === 'A') {
            dh += modifiers.H_N_A;
            ds += modifiers.S_N_A;
          } else if (cThis === 'T') {
            dh += modifiers.H_N_T;
            ds += modifiers.S_N_T;
          } else if (cThis === 'G') {
            dh += modifiers.H_N_G;
            ds += modifiers.S_N_G;
          } else if (cThis === 'C') {
            dh += modifiers.H_N_C;
            ds += modifiers.S_N_C;
          } else {
            dh += modifiers.H_N_N;
            ds += modifiers.S_N_N;
          }
        }
      }

      const deltaH: number = dh * (-100);
      const deltaS: number = ds * -0.1;

      oligoTM1M = deltaH / (deltaS + 1.987 * Math.log(50.0 / 4000000000.0)) - 273.15 + 16.6 * 0;
      oligoTM05M = deltaH / (deltaS + 1.987 * Math.log(50.0 / 4000000000.0)) - 273.15 + 16.6 * -1.30102999566398;
    }

    return {
      mt5Prime: oligoTM05M.toFixed(1),
      mt10Prime: oligoTM1M.toFixed(1),
    };
  }

  private calculateGcPercentage(sequence: string): string {
    const length: number = sequence.length;
    let gc: number = 0;

    if (length === 0) {
      return '0';
    }

    for (let i: number = 0; i < length; i++) {
      const part: string = sequence[i];

      if (part === 'C' || part === 'G') {
        gc++;
      }
    }

    const gcPercentage: number = (gc / length) * 100;

    return gcPercentage.toFixed(0);
  }

  private calculateExtinctionCoefficient(sequence: string): number {
    const seqMap = (part: string): number => {
      const map: SeqMapParam = {
        1: 7.6,
        2: 9.3,
        3: 12.1,
        4: 15.3,
        5: 30.3,
        6: 51.2,
        A: 15.3,
        AC: 7.4,
        AT: 9.3,
        B: 9.5,
        C: 7.4,
        D: 12.1,
        DA: 15.3,
        DB: 9.5,
        DC: 7.4,
        DD: 12.1,
        DG: 11.8,
        DH: 10.7,
        DI: 12.25,
        DK: 10.6,
        DM: 11.4,
        DN: 10.95,
        DR: 13.6,
        DS: 9.6,
        DT: 11.8,
        DV: 11.5,
        DW: 12.3,
        DX: 10.95,
        DY: 8.35,
        E: 11.8,
        F: 15.3,
        FC: 28.4,
        FT: 30.3,
        G: 11.8,
        H: 10.7,
        HC: 39.0,
        HT: 40.9,
        I: 12.25,
        J: 7.6,
        JC: 51.2,
        JT: 51.2,
        K: 10.6,
        M: 11.4,
        MEA: 15.3,
        MEC: 7.3,
        MEG: 11.8,
        MEU: 8.7,
        N: 10.95,
        O: 7.4,
        OA: 15.3,
        OC: 7.3,
        OG: 11.8,
        OU: 8.7,
        Q: 9.3,
        R: 13.6,
        S: 9.6,
        T: 9.3,
        TC: 23.6,
        TT: 25.5,
        U: 8.7,
        V: 11.5,
        W: 12.3,
        X: 10.95,
        Y: 8.35,
        Z: 9.3,
      };

      if (map[part]) {
        return map[part];
      } else {
        return 0;
      }
    };

    const mod5Map = (part: string): number => {
      const map: Mod5MapParam = {
        488: 21.3,
        532: 19.4,
        546: 21.8,
        555: 12.0,
        594: 38.7,
        647: 0.0,
        660: 0.0,
        680: 0.0,
        750: 0.0,
        ACR: 0.0,
        ALD: 0.0,
        ALP: 0.0,
        AMC: 0.0,
        BDA: 0.0,
        BDB: 0.0,
        BDC: 0.0,
        BDE: 0.0,
        BDF: 0.0,
        BDG: 0.0,
        BDH: 0.0,
        BDI: 0.0,
        BDJ: 0.0,
        BDK: 0.0,
        BDL: 0.0,
        BDM: 0.0,
        BDN: 0.0,
        BDP: 0.0,
        BFM: 21.0,
        BHX: 31.6,
        BIO: 0.0,
        BOC: 0.0,
        BP2: 0.0,
        BP3: 0.0,
        BP4: 0.0,
        BTT: 16.2,
        CSB: 0.0,
        CY5: 0.0,
        DIP: 8.5,
        FAM: 21.0,
        FLO: 21.0,
        GWF: 0.0,
        GWR: 0.0,
        HEX: 31.6,
        HRP: 0.0,
        MNB: 0.0,
        OGA: 0.0,
        OGB: 0.0,
        OGC: 0.0,
        OGD: 0.0,
        PFB: 0.0,
        PHO: 0.0,
        PSO: 0.0,
        RGA: 0.0,
        RGB: 0.0,
        RGC: 0.0,
        ROX: 0.0,
        RRA: 0.0,
        TAM: 31.98,
        TET: 16.2,
        THL: 0.0,
        TRX: 0.0,
      };

      if (map[part]) {
        return map[part];
      } else {
        return 0;
      }
    };

    const mod3Map = (part: string): number => {
      const map: Mod3MapParam = {
        ADT: 0.0,
        AM7: 0.0,
        AMN: 0.0,
        BIO: 0.0,
        CPG: 0.0,
        DAB: 0.0,
        TAM: 0.0,
      };

      if (map[part]) {
        return map[part];
      } else {
        return 0;
      }
    };

    const calcTotal = (text: string): number => {
      let calcResult: number = 0;

      for (let i = 0; i < text.length; i++) {
        if (text[i] === '[') {
          while (text[i] !== ']') {
            i++;
            if (i === text.length && text[i] !== ']') {
              return 0;
            }
          }
        } else {
          calcResult += seqMap(text[i]);
        }
      }

      return calcResult;
    };

    let result: number = 0;

    if (sequence) {
      const matches: string[] = sequence.split(',');

      if (matches[0]) {
        result += calcTotal(matches[0]);
      }

      if (matches[1]) {
        result += mod5Map(matches[1]);
      }

      if (matches[2]) {
        result += mod3Map(matches[2]);
      }
    }

    return result;
  }

  private calculateMolecularWeight(sequence: string): number {
    const seqMap = (part: string): number => {
      const map: SeqMapParam = {
        1: 393.4,
        2: 394.4,
        3: 419.4,
        4: 403.4,
        5: 816.71,
        6: 945.66,
        A: 313.2,
        AC: 1421.72,
        AT: 1422.71,
        B: 307.5,
        C: 289.2,
        D: 315.5,
        DA: 313.2,
        DB: 307.5,
        DC: 289.2,
        DD: 315.5,
        DG: 329.2,
        DH: 302.2,
        DI: 313.0,
        DK: 316.7,
        DM: 301.2,
        DN: 309.0,
        DR: 321.2,
        DS: 309.2,
        DT: 304.2,
        DV: 310.5,
        DW: 308.7,
        DX: 309.0,
        DY: 296.7,
        E: 345.3,
        F: 329.3,
        FC: 815.72,
        FT: 816.71,
        G: 329.2,
        H: 302.2,
        HC: 1022.4,
        HT: 1023.39,
        I: 309.0,
        J: 319.3,
        JC: 944.67,
        JT: 945.66,
        K: 316.7,
        M: 301.2,
        MEA: 343.24,
        MEC: 319.21,
        MEG: 359.24,
        MEU: 320.2,
        N: 309.0,
        O: 305.3,
        OA: 343.24,
        OC: 319.21,
        OG: 359.24,
        OU: 320.2,
        Q: 458.41,
        R: 321.2,
        S: 309.2,
        T: 304.2,
        TC: 953.51,
        TT: 954.5,
        U: 290.2,
        V: 310.5,
        W: 308.7,
        X: 309.0,
        Y: 296.7,
        Z: 320.3,
      };

      if (map[part]) {
        return map[part];
      } else {
        return 0;
      }
    };

    const mod5Map = (part: string): number => {
      const map: Mod5MapParam = {
        488: 694.16,
        532: 782.16,
        546: 1122.16,
        555: 993.16,
        594: 884.16,
        647: 1013.16,
        660: 941.16,
        680: 1027.95,
        750: 1054.16,
        ACR: 401.4,
        ALD: 245.14,
        ALP: 0.0,
        AMC: 491.5,
        BDA: 453.28,
        BDB: 577.43,
        BDC: 481.34,
        BDE: 507.35,
        BDF: 527.37,
        BDG: 490.31,
        BDH: 553.4,
        BDI: 566.44,
        BDJ: 698.58,
        BDK: 672.57,
        BDL: 501.33,
        BDM: 614.49,
        BDN: 724.62,
        BDP: 707.57,
        BFM: 554.5,
        BHX: 761.1,
        BIO: 405.0,
        BOC: 438.38,
        BP2: 612.0,
        BP3: 646.0,
        BP4: 546.0,
        BTT: 692.2,
        CSB: 678.6,
        CY5: 0.0,
        DIP: 313.21,
        FAM: 537.46,
        FLO: 537.46,
        GWF: 0.0,
        GWR: 0.0,
        HEX: 744.13,
        HRP: 0.0,
        MNB: 431.38,
        OGA: 0.0,
        OGB: 673.67,
        OGC: 573.5,
        OGD: 686.65,
        PFB: 403.33,
        PHO: 79.98,
        PSO: 420.4,
        RGA: 571.95,
        RGB: 0.0,
        RGC: 685.17,
        ROX: 696.82,
        RRA: 832.96,
        TAM: 591.65,
        TET: 675.24,
        THL: 327.0,
        TRX: 881.06,
      };

      if (map[part]) {
        return map[part];
      } else {
        return 0;
      }
    };

    const mod3Map = (part: string): number => {
      const map: Mod3MapParam = {
        ADT: 0.0,
        AM7: 209.18,
        AMN: 209.18,
        BIO: 569.61,
        CPG: 0.0,
        DAB: 498.49,
        TAM: 591.65,
      };

      if (map[part]) {
        return map[part];
      } else {
        return 0;
      }
    };

    const calcTotal = (text: string): number => {
      let calcResult: number = 0;

      for (let i = 0; i < text.length; i++) {
        if (text[i] === '[') {
          while (text[i] !== ']') {
            i++;
            if (i === text.length && text[i] !== ']') {
              return 0;
            }
          }
        } else {
          calcResult += seqMap(text[i]);
        }
      }

      return calcResult;
    };

    let result: number = 0;

    if (sequence) {
      const matches: string[] = sequence.split(',');

      if (matches[0]) {
        result += calcTotal(matches[0]);
      }

      if (matches[1]) {
        result += mod5Map(matches[1]);
      }

      if (matches[2]) {
        result += mod3Map(matches[2]);
      }
    }

    result -= 61;
    return result;
  }

  private calculateSequenceNmoleOd(sequence: string): string {
    const moleWeight: number = this.calculateMolecularWeight(sequence);
    const extCoef: number = this.calculateExtinctionCoefficient(sequence);
    const result: number = (moleWeight / extCoef) / (moleWeight) * 1000;

    return result.toFixed(1);
  }

  private calculateYield(sequence: string, od: number): YieldParam {
    const moleWeight: number = this.calculateMolecularWeight(sequence);
    const extCoef: number = this.calculateExtinctionCoefficient(sequence);

    const result: YieldParam = {
      ug: (moleWeight / extCoef * od).toFixed(1),
      nmole: (od / extCoef * 1000).toFixed(1),
    };

    return result;
  }
}

export const CALCULATION_SERVICE_NAME = 'calculationService';
export const CalculationServiceFactory = ($q: ng.IQService): CalculationService => {
  return new CalculationServiceImpl($q);
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0-0!./src/main/script/service/calculation-service.ts